import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { normalizePosts } from '../utils/normalizePosts';
import AffiliateCard from '../components/AffiliateCard/AffiliateCard';
import { usePageMeta, SITE_URL, SITE_NAME } from '../hooks/usePageMeta';
import rawPosts from '../data/postsStatic.json';
import './PostPage.css';

// Normalizado uma única vez no carregamento do módulo (ver comentário em
// BlogPage.js): garante que o conteúdo já exista no primeiro render, tanto
// para a pré-renderização quanto para a hidratação no cliente.
const staticPosts = normalizePosts(Array.isArray(rawPosts) ? rawPosts : []);

export default function PostPage() {
  const { slug } = useParams();
  const [posts] = useState(staticPosts);

  const post = posts.find((item) => item.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Metadados por página — importante tanto para SEO quanto para ferramentas
  // de IA/crawlers que leem o HTML pré-renderizado.
  usePageMeta({
    title: post ? `${post.title} | ${SITE_NAME}` : `Post não encontrado | ${SITE_NAME}`,
    description: post?.excerpt,
    path: `/post/${slug}`,
    image: post?.coverImage,
    type: 'article',
    jsonLd: post && {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      image: post.coverImage ? `${SITE_URL}${post.coverImage}` : undefined,
      datePublished: post.publishedAt,
      inLanguage: 'pt-BR',
      articleSection: post.category,
      author: { '@type': 'Person', name: post.author },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
        logo: `${SITE_URL}/logo512.png`
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/post/${slug}`
      }
    }
  });

  if (!post) {
    return (
      <main className="container py-5 text-center">
        <h1 className="mb-3">Post não encontrado</h1>
        <Link to="/" className="btn btn-dark">Voltar ao blog</Link>
      </main>
    );
  }

  const recommendedPosts = posts
    .filter((item) => item.slug !== post.slug)
    .slice(0, 3);

  // Renderiza os children inline de um bloco do Portable Text (texto com
  // marks simples de negrito/itálico), usado tanto por parágrafos quanto
  // por itens de lista.
  const renderInlineChildren = (children, keyPrefix) => {
    return children.map((ch, i) => {
      const key = `${keyPrefix}-ch-${i}`;
      const text = ch.text || '';
      const marks = Array.isArray(ch.marks) ? ch.marks : [];

      let node = text;
      if (marks.includes('strong') || marks.includes('b')) {
        node = <strong key={key}>{node}</strong>;
      } else if (marks.includes('em') || marks.includes('i')) {
        node = <em key={key}>{node}</em>;
      }

      return <span key={key}>{node}</span>;
    });
  };

  const renderContentItem = (item, index) => {
    if (typeof item === 'string') {
      return <p key={index}>{item}</p>;
    }

    if (item.type === 'heading') {
      return <h2 key={index}>{item.text}</h2>;
    }

    if (item.type === 'affiliateAmazonLink') {
      return (
        <AffiliateCard
          key={index}
          title={item.text}
          url={item.url}
          description="Clique para ver o produto na Amazon"
        />
      );
    }

    if (item.type === 'componentHeading') {
      return [
        <hr key={`${index}-top`} className="component-divider" />,
        <h2 key={`${index}-heading`} className="component-heading">
          {item.text}
        </h2>,
        <hr key={`${index}-bottom`} className="component-divider" />
      ];
    }

    if (item.type === 'divider') {
      return <hr key={index} className="my-4" />;
    }

    if (item.type === 'paragraph') {
      // support inline children with marks: [{text, marks:[]}, ...]
      if (Array.isArray(item.children)) {
        return <p key={index}>{renderInlineChildren(item.children, index)}</p>;
      }

      return (
        <p key={index}>
          {item.strong ? <strong>{item.text}</strong> : item.emphasize ? <em>{item.text}</em> : item.text}
        </p>
      );
    }

    if (item.type === 'bulletList' || item.type === 'numberList') {
      const ListTag = item.type === 'numberList' ? 'ol' : 'ul';
      return (
        <ListTag key={index}>
          {item.items.map((children, i) => (
            <li key={`${index}-li-${i}`}>
              {renderInlineChildren(children, `${index}-li-${i}`)}
            </li>
          ))}
        </ListTag>
      );
    }

    return <p key={index}>{item.text}</p>;
  };

  return (
    <main className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8 post-container">

          <article>

            {/* Categoria */}
            <span className="text-uppercase small text-muted">
              {post.category}
            </span>

            {/* Título */}
            <h1 className="post-title mt-2 mb-3">
              {post.title}
            </h1>

            {/* Meta */}
            <div className="text-muted small mb-4 d-flex flex-wrap align-items-center">
              <span>{post.author}</span>
              <span className="mx-2">·</span>
              <time dateTime={post.publishedAt}>
                {new Date(post.publishedAt).toLocaleDateString('pt-BR')}
              </time>
              <span className="mx-2">·</span>
              <span>{post.readingTime}</span>
            </div>

            {/* Imagem */}
            {post.coverImage && (
              <img
                src={post.coverImage}
                alt={post.title}
                className="post-image mb-5"
              />
            )}

            {/* Conteúdo */}
            <div className="post-content">
              {post.content.map(renderContentItem)}
            </div>

          </article>

          {/* Continue lendo */}
          <section className="mt-5 pt-4 border-top">
            <h2 className="h5 mb-4 text-muted">Continue lendo</h2>

            <div className="row">
              {recommendedPosts.map((item) => (
                <div className="col-md-4 mb-3" key={item.id}>
                  <Link to={`/post/${item.slug}`} className="text-decoration-none text-dark">
                    <div className="p-2 h-100 hover-minimal">

                      <small className="text-muted text-uppercase">
                        {item.category}
                      </small>

                      <h3 className="h6 mt-2 mb-2 fw-semibold">
                        {item.title}
                      </h3>

                      <small className="text-muted">
                        {item.readingTime}
                      </small>

                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </section>

          {/* Voltar */}
          <div className="mt-4">
            <Link to="/" className="text-muted text-decoration-none">
              ← Voltar ao blog
            </Link>
          </div>

        </div>
      </div>
    </main>
  );
}